import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { ReceiptUploadGuide, ReceiptPreview } from 'components/cost';
import { useReceiptOcr } from 'hooks/usePayment';
import { paymentStore } from 'store/paymentStore';
import './CostReceipt.css';

export const CostReceipt = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { status, data: ocrData } = useReceiptOcr();

    const [receiptImage, setReceiptImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [extractedData, setExtractedData] = useState<{
        description: string;
        amount: number;
        date: string;
    } | null>(null);

    useEffect(() => {
        if (status === 'ready' && ocrData) {
            setExtractedData({
                description: ocrData.storeName || '지출',
                amount: ocrData.totalAmount || 0,
                date: ocrData.receiptDate ?
                    new Date(ocrData.receiptDate).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).replace(/\. /g, '.').replace(/\.$/, '') :
                    new Date().toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).replace(/\. /g, '.').replace(/\.$/, '')
            });
            setIsProcessing(false);
        }
    }, [status, ocrData]);

    useEffect(() => {
        if (status === 'pending') {
            setIsProcessing(true);
        } else if (status === 'error') {
            setIsProcessing(false);
            setExtractedData({
                description: '영수증',
                amount: 0,
                date: new Date().toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }).replace(/\. /g, '.').replace(/\.$/, '')
            });
        }
    }, [status]);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && meetingId) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setReceiptImage(reader.result as string);
                setIsProcessing(true);

                const formData = new FormData();
                formData.append('receiptImage', file);

                paymentStore.onReceiptOcr(meetingId, formData);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleConfirm = () => {
        navigate(`/cost/${meetingId}`);
    };

    const handleRetake = () => {
        setReceiptImage(null);
        setExtractedData(null);
        setIsProcessing(false);
    };

    return (
        <div className="cost-receipt-wrapper">
            <Header title="영수증 인식" isSubPage />

            <div className="cost-receipt-content">
                {!receiptImage ? (
                    <ReceiptUploadGuide onUpload={handleImageUpload} />
                ) : (
                    <ReceiptPreview
                        receiptImage={receiptImage}
                        isProcessing={isProcessing}
                        extractedData={extractedData}
                    />
                )}
            </div>

            <div className="cost-receipt-footer">
                {receiptImage && extractedData && (
                    <>
                        <button className="retake-button" onClick={handleRetake}>
                            다시 촬영
                        </button>
                        <Button
                            text="확인"
                            onPress={handleConfirm}
                        />
                    </>
                )}
            </div>
        </div>
    );
};
